import type { Message } from '../components/Message';
import type { NoticeData } from '../components/Notice';
import type { Source } from '../components/SourcesList';

export interface ChatState {
  messages: Message[];
  threadKey: string | null;
  turn: number;
  inFlight: boolean;
  notice: NoticeData | null;
}

export type ChatAction =
  | { type: 'send'; userMessage: Message }
  | { type: 'meta'; threadKey?: string; turn?: number; toolCallStarted?: string; retrievedChunks?: Source[] }
  | { type: 'delta'; text: string }
  | { type: 'status'; status: string }
  | { type: 'done'; durationSeconds: number; verbosity: string }
  | { type: 'error'; notice: NoticeData }
  | { type: 'abort' }
  | { type: 'new-conversation' }
  | { type: 'resume'; messages: Message[]; threadKey: string; title?: string | null };

export const initialChatState: ChatState = {
  messages: [],
  threadKey: null,
  turn: 0,
  inFlight: false,
  notice: null,
};

const RAG_TOOL_NAMES = new Set([
  'SearchDocuments',
  'GetDocument',
  'ListDocumentsByLocation',
  'DiscoverFolders',
  'FindRecentChanges',
]);

function statusForTool(toolName?: string): string | null {
  if (!toolName) return null;
  if (RAG_TOOL_NAMES.has(toolName)) return 'Searching documents';
  return 'Consulting Chronos';
}

function mergeSources(existing: Source[] | undefined, incoming: Source[]): Source[] {
  const map = new Map<string, Source>();
  for (const s of existing ?? []) {
    if (s.webViewLink) map.set(s.webViewLink, s);
  }
  for (const s of incoming) {
    if (s.webViewLink && !map.has(s.webViewLink)) map.set(s.webViewLink, s);
  }
  return [...map.values()];
}

/**
 * The "Sources" panel must reflect what the answer actually cited, not the
 * full retrieved set. The model emits inline `[Title](webViewLink)` markdown
 * links; a retrieved chunk is a real source only if its webViewLink appears
 * in the final answer text. Substring containment is enough — Drive
 * webViewLinks are long and unique, and the model reproduces them verbatim.
 */
function citedSources(sources: Source[] | undefined, content: string | undefined): Source[] {
  if (!sources || !content) return [];
  return sources.filter((s) => s.webViewLink && content.includes(s.webViewLink));
}

/**
 * Repair bare-URL citations in a finished `/ask` answer.
 *
 * The model is instructed to cite sources as `[title](webViewLink)` markdown
 * but intermittently emits a *bare* Drive URL instead — either standalone
 * (`https://docs.google.com/document/d/…`) or as the forbidden `Title — URL`
 * form. The bug is the missing `[...]( )` wrapper, not a mangled URL: the link
 * string itself is reproduced verbatim, so we match by exact `webViewLink`.
 *
 * For each retrieved source we rewrite bare occurrences of its `webViewLink`
 * to `[title](webViewLink)`. Sources are processed longest-link first so a
 * tab link (`…&tab=t.x`) is wrapped before the doc-level link that is its
 * prefix. Because we only ever search for *known* link strings, a URL with no
 * retrieved match is left untouched (never a fabricated title) and an
 * already-correct `[title](webViewLink)` link is left alone.
 *
 * Pure — called at `done`, before `citedSources`.
 */
export function normalizeBareCitations(
  content: string | undefined,
  sources: Source[] | undefined,
): string {
  if (!content) return '';
  if (!sources?.length) return content;
  const ordered = [...sources]
    .filter((s) => s.title && s.webViewLink)
    .sort((a, b) => b.webViewLink.length - a.webViewLink.length);
  let out = content;
  for (const { title, webViewLink } of ordered) {
    // Escape `[` / `]` in the title so a bracketed doc name (e.g. "[DRAFT] Q3
    // Plan") produces valid markdown link text rather than a broken link.
    const link = `[${title.replace(/[[\]]/g, '\\$&')}](${webViewLink})`;
    // Pass A — collapse the `Title — URL` form. Requires the chunk's exact
    // title immediately before the separator, so it can never eat prose.
    for (const sep of [' — ', ' – ', ' - ']) {
      out = out.split(`${title}${sep}${webViewLink}`).join(link);
    }
    // Pass B — wrap a remaining bare URL.
    out = wrapBareUrl(out, webViewLink, link);
  }
  return out;
}

/**
 * Replace every occurrence of `url` in `text` with `replacement`, except
 * occurrences already inside a markdown link — i.e. preceded by `](` (an
 * href) or `[` (link text). Plain string scan, no regex lookbehind (older
 * iOS Safari rejects lookbehind at parse time).
 */
function wrapBareUrl(text: string, url: string, replacement: string): string {
  let out = '';
  let from = 0;
  let i = text.indexOf(url, from);
  while (i >= 0) {
    const alreadyLinked = text.slice(Math.max(0, i - 2), i) === '](' || text[i - 1] === '[';
    out += alreadyLinked
      ? text.slice(from, i + url.length)
      : text.slice(from, i) + replacement;
    from = i + url.length;
    i = text.indexOf(url, from);
  }
  return out + text.slice(from);
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'send':
      return {
        ...state,
        messages: [
          ...state.messages,
          action.userMessage,
          { id: crypto.randomUUID(), role: 'assistant', content: '', status: 'Thinking…' },
        ],
        inFlight: true,
        notice: null,
      };
    case 'meta': {
      const next: ChatState = {
        ...state,
        threadKey: action.threadKey ?? state.threadKey,
        turn: action.turn ?? state.turn,
      };
      const last = next.messages[next.messages.length - 1];
      if (!last || last.role !== 'assistant') return next;
      const sources = action.retrievedChunks?.length
        ? mergeSources(last.sources, action.retrievedChunks)
        : last.sources;
      const hasDelta = !!last.content;
      let status = last.status;
      if (!hasDelta) {
        const toolLabel = statusForTool(action.toolCallStarted);
        if (toolLabel) {
          status = `${toolLabel}…`;
        } else if (sources && sources.length > 0) {
          const n = sources.length;
          status = `Reading ${n} source${n > 1 ? 's' : ''}…`;
        } else {
          status = 'Working…';
        }
      }
      const updated: Message = { ...last, sources, status };
      return { ...next, messages: [...next.messages.slice(0, -1), updated] };
    }
    case 'delta': {
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'assistant') return state;
      const updated: Message = { ...last, content: (last.content ?? '') + action.text, status: undefined };
      return { ...state, messages: [...state.messages.slice(0, -1), updated] };
    }
    case 'status': {
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'assistant') return state;
      if (last.content) return state;
      return {
        ...state,
        messages: [...state.messages.slice(0, -1), { ...last, status: action.status }],
      };
    }
    case 'done': {
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'assistant') return { ...state, inFlight: false };
      // Repair bare-URL citations before deciding which sources were cited:
      // citedSources matches on the webViewLink substring, which a rewritten
      // [title](link) still contains — so a previously-bare citation now also
      // registers correctly in the Sources panel.
      const content = last.content
        ? normalizeBareCitations(last.content, last.sources)
        : last.content;
      const updated: Message = {
        ...last,
        content,
        status: undefined,
        sources: citedSources(last.sources, content),
        meta: {
          threadKey: state.threadKey ?? undefined,
          turn: state.turn || undefined,
          verbosity: action.verbosity,
          durationSeconds: action.durationSeconds,
        },
      };
      return { ...state, messages: [...state.messages.slice(0, -1), updated], inFlight: false };
    }
    case 'error': {
      // Attach the notice to the failed assistant message in-place (clearing
      // its "Thinking…" pill) rather than rendering a separate top-level
      // banner. Matches Phase 1 chat.js, which inlines the notice in the
      // assistant bubble — keeps the conversation in flow instead of leaving
      // a stale spinner stranded above the message list when retries 429.
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'assistant') {
        return { ...state, inFlight: false, notice: action.notice };
      }
      const updated: Message = { ...last, status: undefined, notice: action.notice };
      return {
        ...state,
        inFlight: false,
        notice: null,
        messages: [...state.messages.slice(0, -1), updated],
      };
    }
    case 'abort': {
      // User pressed Stop while a request was in flight. If the assistant
      // hadn't emitted any content yet, drop the empty placeholder so the
      // conversation doesn't end with a stranded "Thinking…" pill. If
      // partial content streamed in, keep what we got — the user might
      // want to read it — but clear the spinner.
      const last = state.messages[state.messages.length - 1];
      if (!last || last.role !== 'assistant') {
        return { ...state, inFlight: false };
      }
      if (!last.content) {
        return { ...state, inFlight: false, messages: state.messages.slice(0, -1) };
      }
      const updated: Message = { ...last, status: undefined };
      return { ...state, inFlight: false, messages: [...state.messages.slice(0, -1), updated] };
    }
    case 'new-conversation':
      return { ...initialChatState };
    case 'resume': {
      // Load a persisted thread from GET /conversations/{threadKey}. Reset to
      // a known-clean state then set the messages + threadKey + count the
      // user-role turns so the next /ask carries the right turn number.
      return {
        ...initialChatState,
        messages: action.messages,
        threadKey: action.threadKey,
        turn: action.messages.filter(m => m.role === 'user').length,
      };
    }
  }
}
